import React from 'react';
import { getUnifiedTimestamp } from '../utils/combinedMessageUtils';

export const MessageList = ({ contacts, selectedContact, onSelectContact }) => {
  const getInitials = (name) => {
    if (!name || name === 'Unknown') return '?';

    if (name.includes(' ')) {
      const words = name.trim().split(/\s+/).filter(w => w.length > 0);
      if (words.length === 2 && words[1].match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/)) {
        // Treat as single word with trailing emoji
        const fullName = words.join('');
        const emojiRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
        const emojiMatch = fullName.match(emojiRegex);
        const emoji = emojiMatch ? emojiMatch[0] : '';
        const textWithoutEmoji = fullName.replace(emojiRegex, '').trim();
        const firstLetter = textWithoutEmoji.length > 0 ? textWithoutEmoji.charAt(0).toUpperCase() : '';
        if (!firstLetter && emoji) return emoji;
        if (!emoji) return textWithoutEmoji.substring(0, 2).toUpperCase();
        return `${firstLetter}${emoji}`;
      } else if (words.length >= 2) {
        // Normal multi-word
        return words[0][0] + words[words.length - 1][0];
      }
    }

    // For single words without space
    const emojiRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
    const emojiMatch = name.match(emojiRegex);
    const emoji = emojiMatch ? emojiMatch[0] : '';
    const textWithoutEmoji = name.replace(emojiRegex, '').trim();
    const firstLetter = textWithoutEmoji.length > 0 ? textWithoutEmoji.charAt(0).toUpperCase() : '';

    if (!firstLetter && emoji) return emoji;
    if (!emoji) return textWithoutEmoji.substring(0, 2).toUpperCase();
    return `${firstLetter}${emoji}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    // Use unified timestamp for both incoming and outgoing messages
    const date = new Date(getUnifiedTimestamp(timestamp));
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getMessageTypeDisplay = (type) => {
    const typeMap = {
      'text': '📝 Text',
      'button': '🔘 Button',
      'image': '🖼️ Image',
      'video': '🎥 Video',
      'audio': '🎵 Audio',
      'document': '📄 Document',
      'location': '📍 Location',
      'contact': '👤 Contact',
      'interactive': '🎯 Interactive',
      'template': '📋 Template',
      'media': '📎 Media',
      'WhatsApp Chat': '💬 WhatsApp',
      'SMS': '📱 SMS',
      'Email': '📧 Email',
      'Notification': '🔔 Notification',
      'System': '⚙️ System'
    };

    return typeMap[type] || '📤 Message';
  };

  return (
    <div className="space-y-1">
      {contacts.map(contact => {
        const lastMsg = contact.lastMessage;
        const isActive = selectedContact === contact.phone;
        const initials = getInitials(contact.name);
        const time = formatTime(lastMsg?.timestamp || lastMsg?.unified_timestamp);
        const typeDisplay = getMessageTypeDisplay(lastMsg?.type || 'text');
        const hasOutgoing = contact.hasOutgoing;
        const hasIncoming = contact.hasIncoming;
        const lastDirection = lastMsg?.direction || 'incoming';
        const processName = lastMsg?.meta?.process_name || '';

        return (
          <div
            key={contact.phone}
            onClick={() => {
              onSelectContact(contact.phone);
            }}
            className={`p-4 border rounded-lg cursor-pointer transition-all ${
              isActive
                ? 'bg-teal-50 border-teal-500'
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-normal ${
                isActive ? 'bg-teal-500' : 'bg-gray-400'
              }`} style={{fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif', fontSize: '24px'}}>
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-base ${isActive ? 'text-teal-900' : 'text-gray-900'}`} style={{fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif'}}>
                  {contact.name}
                  {/* Direction indicators */}
                  <span className="ml-2 text-xs">
                    {hasIncoming && hasOutgoing && '👥'}
                    {hasOutgoing && !hasIncoming && '📤'}
                    {hasIncoming && !hasOutgoing && '📥'}
                  </span>
                </div>
                <div className="text-base text-gray-500 truncate">
                  {lastDirection === 'outgoing' ? '📤' : '📥'} {processName || typeDisplay} {lastMsg?.body || ''}
                </div>
              </div>
              <div className="text-sm text-gray-400">
                {time}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageList;
